import { MutableRefObject, useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/** Protect unsent support text/files when BrowserRouter changes routes or the document unloads. */
export function useSupportDraftGuard(dirty: MutableRefObject<boolean>, label: string) {
  const location = useLocation();
  const browserLocation = useRef({ url: window.location.href, state: window.history.state });
  const discardLabel = useRef(label);
  discardLabel.current = label;
  useLayoutEffect(() => {
    browserLocation.current = { url: window.location.href, state: window.history.state };
  }, [location.key, location.pathname, location.search, location.hash]);
  useEffect(() => {
    let restoring = false;
    const unload = (event: BeforeUnloadEvent) => {
      if (dirty.current) { event.preventDefault(); event.returnValue = ''; }
    };
    // Capture before BrowserRouter's listener, including Back that unmounts the page.
    const pop = (event: PopStateEvent) => {
      if (restoring) { restoring = false; event.stopImmediatePropagation(); return; }
      if (!dirty.current) return;
      if (window.confirm(discardLabel.current)) { dirty.current = false; return; }
      event.stopImmediatePropagation();
      const previous = browserLocation.current;
      const delta = previous.state?.idx - event.state?.idx;
      if (Number.isFinite(delta) && delta !== 0) { restoring = true; window.history.go(delta); }
      else window.history.pushState(previous.state, '', previous.url);
    };
    const link = (event: MouseEvent) => {
      if (!dirty.current || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const anchor = event.target instanceof Element ? event.target.closest<HTMLAnchorElement>('a[href]') : null;
      if (!anchor || anchor.hasAttribute('download') || (anchor.target && anchor.target !== '_self')) return;
      const destination = new URL(anchor.href, window.location.href);
      if (!['http:', 'https:'].includes(destination.protocol)) return;
      const current = new URL(window.location.href);
      if (destination.origin === current.origin && destination.pathname === current.pathname && destination.search === current.search) return;
      if (window.confirm(discardLabel.current)) { dirty.current = false; return; }
      event.preventDefault(); event.stopPropagation();
    };
    window.addEventListener('beforeunload', unload);
    window.addEventListener('popstate', pop, true);
    document.addEventListener('click', link, true);
    return () => {
      window.removeEventListener('beforeunload', unload);
      window.removeEventListener('popstate', pop, true);
      document.removeEventListener('click', link, true);
    };
  }, [dirty]);
}
